import { defineStore } from 'pinia'
import { checkAndHandleNpcVictory } from '@/map/logic/utils/plotlogic/npcPlot'
import axios from '@/axios'
import { useAudioStore } from '@/stores/audioStore'
import { useDisplayStore } from '@/stores/displayStore'
import { reactive, watch } from 'vue'

// 顶部：通用图片预加载工具（只保留在局部，不长期占内存）
async function preloadImages(urls) {
  if (!Array.isArray(urls) || urls.length === 0) return

  return Promise.all(
    urls.map((url) =>
      new Promise((resolve) => {
        const img = new window.Image()
        img.onload = () => resolve(url)
        img.onerror = () => {
          console.warn('图片加载失败', url)
          resolve(url)
        }
        img.src = url
      })
    )
  )
}

// ★ 序列帧相关常量（和 PokemonImagePanel 保持一致）
const SEQ_BASE_URL = 'https://wudi-1308828739.cos.ap-beijing.myqcloud.com/out35'
// 旧的 action 集合先保留，不再直接用 anchors.json 了
const SEQ_SETS = ['idle', 'physical', 'special', 'elemental', 'hit']

function pad2(n) {
  return String(n).padStart(2, '0')
}

export const usePveStore = defineStore('pve', {
  state: () => ({
    uiImages: {
      playerStatusBoxBg:
        'https://joygma.online/ui/pvebattle/statusbox/bg1.png'
      // 以后还可以继续加，比如 battleBtn: '', switchBtn: '', ...
    },
    backgroundUrl:
      'https://joygma.online/ui/pvebattle/background/normal.png',

    currentView: 'loading', // 控制主界面：loading / battle / result
    opponentConfig: null, // 对手配置
    context: null, // 战斗上下文

    playerInfo: {
      self: {
        id: null,
        avatarUrl: null
      },
      opponent: {
        id: null,
        avatarUrl: null
      }
    },

    isLoadingComplete: false, // 是否加载完毕
    progress: 0, // 加载进度条百分比

    // 战斗倒计时
    turnTimer: {
      value: 30,
      status: 'idle' // 'idle' | 'counting' | 'timeout'
    },

    // 操作面板（四个按钮）
    operationUI: {
      currentPanel: 'none', // 'none' | 'move' | 'item' | 'switch' | 'run'
      isLocked: false // true 表示已经提交操作，禁止再点
    },

    // 玩家选择的动作
    selectedAction: null, // { type, data }

    // 回合动画触发器
    turnAnimationTrigger: {
      id: Date.now(),
      firstActor: 'player', // 'player' | 'opponent'
      firstAction: {
        actor: 'player',
        type: 'physical', // 'physical' | 'special' | 'status' | 'switch'
        target: 'opponent',
        moveIndex: 0
      },
      secondAction: {
        actor: 'opponent',
        type: 'physical',
        target: 'player',
        moveIndex: 0
      },
      status: 'done' // 'playing' | 'done'
    },

    // 阵亡动画触发器
    faintAnimationTrigger: {
      id: 0,
      player1Fainted: false,
      player2Fainted: false,
      status: 'done'
    },

    // 出场动画是否已播放
    hasPlayedEntryAnimation: false,
    hasopponentEntryAnimation: false,

    // 通用动画触发器（出场等）
    animationTrigger: {
      type: '', // 'entry' 等
      target: '', // 'player' | 'opponent'
      id: 0,
      status: 'idle' // 'idle' | 'playing' | 'done'
    },

    battleType: 'wild', // 'wild' | 'trainer' | 'boss'
    currentLevelId: null, // 当前对战塔关卡编号

    // ★ 新增：倒计时定时器句柄，避免成为“野生全局变量”
    _timer: null
  }),

  actions: {
    setCurrentLevelId(levelId) {
      this.currentLevelId = levelId
    },

    setCurrentView(view) {
      this.currentView = view
    },

    setSelfPlayer(selfId) {
      this.playerInfo.self.id = selfId
      this.playerInfo.self.avatarUrl = `https://joygma.online/user/avatar/${selfId}`
    },

    setOpponentPlayer(opponentId) {
      this.playerInfo.opponent.id = opponentId
    
      // wildmap 开头 → 用宝可梦头像
      if (typeof opponentId === 'string' && opponentId.startsWith('wildmap')) {
        const numPart = opponentId.slice('wildmap'.length) // wildmap2014 → '2014'
        const pokeId = parseInt(numPart, 10)
    
        if (!Number.isNaN(pokeId)) {
          const ext = pokeId <= 2000 ? 'png' : 'webp'
          this.playerInfo.opponent.avatarUrl =
            `https://joygma.online/images/pokemon/icons/${pokeId}.${ext}`
          return
        }
      }
    
      // 其他对手：走原来的 pve_opponents 头像
      this.playerInfo.opponent.avatarUrl =
        `https://joygma.online/images/pve_opponents/${opponentId}.png`
    },
    
    

    // 设置 / 清空对手配置
    setOpponentConfig(config) {
      this.opponentConfig = config
    },
    clearOpponentConfig() {
      this.opponentConfig = null
    },

    // 设置 / 清空战斗上下文
    setContext(context) {
      this.context = reactive(structuredClone(context))
    },
    clearContext() {
      this.context = null
    },

    clearPlayerInfo() {
      this.playerInfo = {
        self: { id: null, avatarUrl: null },
        opponent: { id: null, avatarUrl: null }
      }
    },

    // ======================
    // ★ 序列帧预加载逻辑（atlas 版）
    // ======================

    // 预加载某一只宝可梦的 atlas 序列帧
    // 目录结构：/out10/aaa/{idStr}/atlas.json + atlas_0.webp(, atlas_1.webp …)
    async preloadSequenceFramesForPokemonId(pokemonId) {
      if (pokemonId === null || pokemonId === undefined) return

      const displayStore = useDisplayStore()
      const baseUrl = displayStore.isHighResolution
        ? 'https://wudi-1308828739.cos.ap-beijing.myqcloud.com/out100'
        : SEQ_BASE_URL

      const idStr = pad2(pokemonId)
      const folder = `${baseUrl}/${idStr}`
      const atlasUrl = `${folder}/atlas.json`
      let atlas

      try {
        const res = await fetch(atlasUrl)
        if (!res.ok) {
          console.warn('⚠️ atlas.json 加载失败:', atlasUrl)
          return
        }
        atlas = await res.json()
      } catch (err) {
        console.warn('❌ atlas.json 解析失败:', atlasUrl, err)
        return
      }

      if (!Array.isArray(atlas.frames) || atlas.frames.length === 0) {
        console.warn('⚠️ atlas.json 中没有 frames:', atlasUrl)
        return
      }

      const pageCount = atlas.meta?.page_count ?? 1
      const atlasImages = []

      for (let i = 0; i < pageCount; i++) {
        atlasImages.push(`${folder}/atlas_${i}.webp`)
      }

      await preloadImages(atlasImages)

      const actionCount = {}
      for (const f of atlas.frames) {
        const act = f.action || 'unknown'
        actionCount[act] = (actionCount[act] || 0) + 1
      }

      console.log(
        `✅ atlas 预加载完成: 宝可梦ID=${pokemonId}，总帧数=${atlas.frames.length}，动作分布=`,
        actionCount
      )
    },

    // 从当前 context 中解析本局所有会用到的宝可梦 ID，并依次预加载它们的序列帧（atlas）
    async preloadSequenceFramesFromContext() {
      const bc = this.context?.battleContext
      if (!bc) {
        console.warn('⚠️ 当前没有 battleContext，无法预加载序列帧')
        return
      }

      const idSet = new Set()
      const p1Team = Array.isArray(bc.player1?.team) ? bc.player1.team : []
      const p2Team = Array.isArray(bc.player2?.team) ? bc.player2.team : []

      p1Team.forEach((poke) => {
        if (poke && poke.pokemonId != null) {
          idSet.add(poke.pokemonId)
        }
      })
      p2Team.forEach((poke) => {
        if (poke && poke.pokemonId != null) {
          idSet.add(poke.pokemonId)
        }
      })

      if (!idSet.size) {
        console.log('ℹ️ 当前 context 中未找到可预加载序列帧的宝可梦ID')
        return
      }

      const idList = Array.from(idSet)
      console.log(
        `🚀 准备预加载 PVE atlas 序列帧，宝可梦数量: ${idList.length}，ID 列表:`,
        idList
      )

      // 顺序加载，避免一次性起太多请求打爆浏览器；真想暴力可以 Promise.all
      for (const id of idList) {
        await this.preloadSequenceFramesForPokemonId(id)
      }

      console.log('🎬 PVE atlas 序列帧预加载全部完成')
    },

    // 这个函数已经在 startProgressLoop 中调用，加载界面只用调这一条
    async startFullPveBattle() {
      try {
        const playerId = this.playerInfo.self.id
        const enemyId = this.playerInfo.opponent.id

        // 加载 context
        const loadRes = await axios.post('/pve/load', { playerId, enemyId })
        this.setContext(loadRes.data.context)

        console.log('加载到的 context:', this.context)

        // 出场阶段
        const startRes = await axios.post('/pve/gamestart', { playerId })
        this.setContext(startRes.data.context)

        // =======================
        // ✅ 预加载 UI + 序列帧
        // =======================

        // 1）UI 图片预加载（背景、状态框等）
        const uiUrls = [
          this.backgroundUrl,
          this.uiImages.playerStatusBoxBg
          // 后面如果有更多 UI 图，在这里往数组里 push 即可
        ].filter(Boolean)

        if (uiUrls.length) {
          await preloadImages(uiUrls)
          console.log('✅ UI 图片预加载完成：', uiUrls)
        }

        // 2）序列帧预加载（atlas）
        await this.preloadSequenceFramesFromContext()

        // 标记加载完成
        this.isLoadingComplete = true
        return true
      } catch (err) {
        console.error('❌ PvE 启动失败：', err)
        return false
      }
    },

    async startProgressLoop() {
      this.progress = 0
      this.isLoadingComplete = false

      // 根据对手 ID 推断战斗类型
      const opponentId = this.playerInfo.opponent.id
      if (typeof opponentId === 'string') {
        if (opponentId.startsWith('trainer') || opponentId.startsWith('maptrainer')) {
          this.battleType = 'trainer'
        } else if (opponentId.startsWith('boss')) {
          this.battleType = 'boss'
        } else {
          this.battleType = 'wild'
        }
      } else {
        this.battleType = 'wild'
      }

      // ✅ 等待数据加载完成再配合进度条
      await this.startFullPveBattle()

      const interval = setInterval(() => {
        if (this.progress < 100) {
          this.progress += 5
        }

        if (this.progress >= 100 && this.isLoadingComplete) {
          clearInterval(interval)
          this.setCurrentView('battle')
        }
      }, 200)
    },

    // ======================
    // 倒计时相关
    // ======================
    startTurnTimer() {
      // 重置状态
      this.turnTimer.value = 30
      this.turnTimer.status = 'counting'

      // 清除旧的定时器
      if (this._timer) {
        clearInterval(this._timer)
      }

      // 启动新的倒计时
      this._timer = setInterval(() => {
        if (this.turnTimer.value > 0) {
          this.turnTimer.value--
        } else {
          clearInterval(this._timer)
          this._timer = null
          this.turnTimer.status = 'timeout'
        }
      }, 1000)
    },

    resetTurnTimer() {
      if (this._timer) {
        clearInterval(this._timer)
        this._timer = null
      }
      this.turnTimer.value = 30
      this.turnTimer.status = 'idle'
    },

    // ======================
    // 操作面板控制
    // ======================
    setOperationPanel(panelName) {
      if (this.operationUI.isLocked) return
      this.operationUI.currentPanel = panelName
    },

    setfaintedswitchOperationPanel(panelName) {
      this.operationUI.currentPanel = panelName
    },

    lockOperation() {
      this.operationUI.isLocked = true
    },

    unlockOperation() {
      this.operationUI.isLocked = false
      this.operationUI.currentPanel = 'none'
    },

    // ======================
    // 行动选择
    // ======================
    selectMoveAction(moveIndex) {
      this.selectedAction = { type: 'move', data: moveIndex }
      this.resetTurnTimer()
      this.operationUI.currentPanel = 'none'
      this.operationUI.isLocked = true
      console.log('已选择技能操作：', this.selectedAction)
    },

    chooseSwitchPokemon(index) {
      this.selectedAction = { type: 'switch', data: index }
      this.resetTurnTimer()
      this.operationUI.currentPanel = 'none'
      this.operationUI.isLocked = true
      console.log('已选择切换操作：', this.selectedAction)
    },

    selectRunAction() {
      this.selectedAction = { type: 'run', data: true }
      this.resetTurnTimer()
      this.operationUI.currentPanel = 'none'
      this.operationUI.isLocked = true
      console.log('已选择逃跑操作：', this.selectedAction)
    },

    selectUseItemAction(itemId, isCatch = false) {
      this.selectedAction = {
        type: isCatch ? 'catch' : 'item',
        data: itemId
      }
      this.resetTurnTimer()
      this.operationUI.currentPanel = 'none'
      this.operationUI.isLocked = true
      console.log(`已选择使用${isCatch ? '捕捉' : '道具'}操作：`, this.selectedAction)
    },

    // ======================
    // 回合主循环
    // ======================
    async startTurnCycle() {
      const playerId = this.playerInfo.self.id
      const audioStore = useAudioStore()

      const player1pokeId =
        this.context.battleContext.player1.activePokemon.pokemonId
      const player2pokeId =
        this.context.battleContext.player2.activePokemon.pokemonId

      // 第一次回合：播放玩家出场动画
      if (!this.hasPlayedEntryAnimation) {
        this.animationTrigger = {
          type: 'entry',
          target: 'player',
          id: Date.now(),
          status: 'playing'
        }

        audioStore.playPokemonCry(player1pokeId)

        while (this.animationTrigger.status !== 'done') {
          await new Promise((resolve) => setTimeout(resolve, 30))
        }

        this.hasPlayedEntryAnimation = true
      }

      // 第一次回合：播放对手出场动画
      if (!this.hasopponentEntryAnimation) {
        this.animationTrigger = {
          type: 'entry',
          target: 'opponent',
          id: Date.now(),
          status: 'playing'
        }

        audioStore.playPokemonCry(player2pokeId)

        while (this.animationTrigger.status !== 'done') {
          await new Promise((resolve) => setTimeout(resolve, 30))
        }

        this.hasopponentEntryAnimation = true
      }

      // 回合准备
      const startRes = await axios.post('/pve/turnstart', { playerId })
      this.setContext(startRes.data.context)

      // 清空上一回合操作，启动倒计时
      this.selectedAction = null
      this.unlockOperation()
      this.startTurnTimer()
      this.setOperationPanel('move')

      // 等待操作或倒计时结束
      const action = await this.waitForActionOrTimeout()

      // 锁定 & 提交操作
      this.lockOperation()
      const actionRes = await axios.post('/pve/action', {
        playerId,
        action
      })
      this.setContext(actionRes.data.context)

      await this.handleTurnAnimationTrigger()
      console.log(
        '🎬 turnAnimationTrigger:',
        JSON.parse(JSON.stringify(this.turnAnimationTrigger))
      )

      // 回合结束
      const endRes = await axios.post('/pve/turnend', { playerId })
      this.setContext(endRes.data.context)

      if (endRes.data.gameOver) {
        this.setCurrentView('result')
        return
      }

      // 阵亡后的切换逻辑
      if (endRes.data.fainted?.player1Fainted || endRes.data.fainted?.player2Fainted) {
        const fainted = endRes.data.fainted
        let playerSwitchAction = null

        // 播放倒下动画
        await this.handleFaintAnimation(fainted)

        // 玩家自己阵亡，进入强制切换
        if (fainted.player1Fainted) {
          playerSwitchAction = await this.waitForPlayerSwitch(fainted)
        } else {
          this.selectedAction = null
        }

        // 提交切换数据给后端
        const faintsRes = await axios.post('/pve/faintswith', {
          playerId,
          faintedList: fainted,
          playerSwitchAction
        })

        // 更新 context
        this.setContext(faintsRes.data.context)

        if (fainted.player1Fainted) {
          this.triggerAnimation('entry', 'player')
          audioStore.playPokemonCry(player1pokeId)
          while (this.animationTrigger.status !== 'done') {
            await new Promise((resolve) => setTimeout(resolve, 30))
          }
        }

        if (fainted.player2Fainted) {
          this.triggerAnimation('entry', 'opponent')
          audioStore.playPokemonCry(player2pokeId)
          while (this.animationTrigger.status !== 'done') {
            await new Promise((resolve) => setTimeout(resolve, 30))
          }
        }

        // 强制切换后如果直接结束
        if (faintsRes.data.context.isGameOver) {
          this.setCurrentView('result')
          return
        }
      }

      // 继续下一回合
      await this.startTurnCycle()
    },

    // ✅ 等待玩家操作或倒计时超时
    waitForActionOrTimeout() {
      return new Promise((resolve) => {
        const timeoutChecker = setInterval(() => {
          if (this.turnTimer.status === 'timeout') {
            clearInterval(timeoutChecker)
            stop()
            this.lockOperation()
            resolve({ type: 'run', data: true }) // 超时默认逃跑
          }
        }, 500)

        const stop = watch(
          () => this.selectedAction,
          (val) => {
            if (val !== null) {
              // 玩家已经选好了操作，清掉超时检查
              clearInterval(timeoutChecker)
              stop()
              this.resetTurnTimer()
              resolve(val)
            }
          }
        )
      })
    },

    // ✅ 等待强制切换（我方宝可梦阵亡）
    waitForPlayerSwitch(faintedList) {
      return new Promise((resolve) => {
        this.selectedAction = null

        // 锁住按钮，显示“切换精灵”界面
        this.lockOperation()
        this.setfaintedswitchOperationPanel('switch')

        // 开启倒计时
        this.startTurnTimer()

        const timeoutChecker = setInterval(() => {
          if (this.turnTimer.status === 'timeout') {
            clearInterval(timeoutChecker)
            stop()
            resolve({ type: 'run' }) // 超时默认逃跑
          }
        }, 500)

        const stop = watch(
          () => this.selectedAction,
          (val) => {
            if (val !== null && val.type === 'switch') {
              clearInterval(timeoutChecker)
              stop()
              this.resetTurnTimer()
              resolve(val)
            }
          }
        )
      })
    },

    async endBattleAndCleanup() {
      try {
        const playerId = this.playerInfo.self.id
        const res = await axios.post('/pve/battleend', { playerId })
        this.setContext(res.data.context)

        await checkAndHandleNpcVictory(this.context, this.playerInfo)

        console.log('✅ 战斗已结算并清除上下文')
        if (res.data.context?.settlement?.log) {
          console.log(
            '📜 结算日志:\n' +
              res.data.context.settlement.log.join('\n')
          )
        }

        // 如果玩家赢了且当前关卡有效，就尝试更新塔进度
        if (
          this.context?.winnerId === this.playerInfo.self.id &&
          this.currentLevelId !== null
        ) {
          try {
            const updateRes = await axios.post('/tower/update', {
              userId: playerId,
              newProgress: this.currentLevelId + 1
            })
            console.log('✅ 尝试更新对战塔进度：', updateRes.data)
          } catch (err) {
            console.error('❌ 更新塔进度失败:', err)
          }
        }
      } catch (err) {
        console.error('❌ 战斗结算失败：', err)
      }

      // 通知序列帧面板释放资源（包括 imageCache 在内的监听者）
      window.dispatchEvent(new CustomEvent('pve:release-sequence-frames'))
    },

    triggerAnimation(type, target) {
      this.animationTrigger = {
        type,
        target,
        id: Date.now(),
        status: 'playing'
      }
    },

    finishAnimation() {
      this.animationTrigger.status = 'done'
    },

    setBattleType(type) {
      this.battleType = type // 'wild' | 'trainer' | 'boss'
    },

    // 解析本回合动画需要的信息
    async handleTurnAnimationTrigger() {
      const { player1Action, player2Action, firstToAct, battleContext } =
        this.context
      const firstActorStr = firstToAct.playerStr
      const secondActorStr = firstActorStr === 'player1' ? 'player2' : 'player1'

      const extractActionDetails = (actorStr, action) => {
        if (!action) return null

        if (action.type === 'move') {
          const moveSlot = action.data
          const actorPokemon = battleContext[actorStr]?.activePokemon
          const move = actorPokemon?.moves?.[moveSlot]
          if (!move) return null

          return {
            actor: actorStr === 'player1' ? 'player' : 'opponent',
            type:
              move.category === 'PHYSICAL'
                ? 'physical'
                : move.category === 'SPECIAL'
                ? 'special'
                : 'status',
            target: move.target === 'OPPONENT' ? 'opponent' : 'self',
            moveIndex: moveSlot,
            moveName: move.displayName
          }
        } else if (action.type === 'switch') {
          return {
            actor: actorStr === 'player1' ? 'player' : 'opponent',
            type: 'switch',
            target: 'self',
            moveIndex: null
          }
        } else if (action.type === 'catch') {
          return {
            actor: actorStr === 'player1' ? 'player' : 'opponent',
            type: 'catch',
            target: 'opponent',
            moveIndex: null
          }
        }

        return null
      }

      const firstAction = extractActionDetails(
        firstActorStr,
        this.context[firstActorStr + 'Action']
      )
      const secondAction = extractActionDetails(
        secondActorStr,
        this.context[secondActorStr + 'Action']
      )

      this.turnAnimationTrigger = {
        id: Date.now(),
        firstActor: firstAction?.actor || 'player',
        firstAction,
        secondAction,
        status: 'playing'
      }

      while (this.turnAnimationTrigger.status !== 'done') {
        await new Promise((resolve) => setTimeout(resolve, 30))
      }
    },

    async handleFaintAnimation(faintedInfo) {
      this.faintAnimationTrigger = {
        id: Date.now(),
        player1Fainted: faintedInfo.player1Fainted,
        player2Fainted: faintedInfo.player2Fainted,
        status: 'playing'
      }

      while (this.faintAnimationTrigger?.status !== 'done') {
        await new Promise((resolve) => setTimeout(resolve, 30))
      }

      this.faintAnimationTrigger = null
    },

    // 重置整场战斗状态（一般在退出 PVE 时调用）
    resetBattleState() {
      this.clearContext()
      this.clearOpponentConfig()
      this.clearPlayerInfo()

      this.currentView = 'loading'
      this.isLoadingComplete = false
      this.progress = 0

      this.resetTurnTimer()

      this.operationUI = {
        currentPanel: 'none',
        isLocked: false
      }
      this.selectedAction = null

      this.animationTrigger = {
        type: '',
        target: '',
        id: 0,
        status: 'idle'
      }

      this.turnAnimationTrigger = {
        id: Date.now(),
        firstActor: 'player',
        firstAction: {
          actor: 'player',
          type: 'physical',
          target: 'opponent',
          moveIndex: 0
        },
        secondAction: {
          actor: 'opponent',
          type: 'physical',
          target: 'player',
          moveIndex: 0
        },
        status: 'done'
      }

      this.faintAnimationTrigger = {
        id: 0,
        player1Fainted: false,
        player2Fainted: false,
        status: 'done'
      }

      this.hasPlayedEntryAnimation = false
      this.hasopponentEntryAnimation = false
      this.currentLevelId = null
      this.battleType = 'wild'

      // ★ 兜底：如果直接 reset（没走 endBattleAndCleanup），也广播一次释放事件
      try {
        window.dispatchEvent(new CustomEvent('pve:release-sequence-frames'))
      } catch (e) {
        console.warn('广播 pve:release-sequence-frames 失败', e)
      }
    }
  }
})
